import React, { isValidElement, memo, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { sendClick } from 'reaxl-analytics';
import { InventoryListingOverlayActions } from 'reaxl-listing';

import { saveVDPListing } from '@/utilities/saveListing';

import authDuck from '@/ducks/authDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import ShareContentContainer from '@/containers/ShareContentContainer';

export function SaveAndShareContainer({ children, uiContext }) {

    const {
        share: [isShareEnabled],
    } = useFeatures([
        'share',
    ]);

    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const savedListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds);
    const isSaved = savedListingIds?.includes(listing.id) ?? false;

    const dispatch = useDispatch();

    // retrieve saved inventory ids and set them in the inventory reducer
    const initializeUserInfo = useCallback(() => {
        dispatch(userDuck.creators.hydrateUserInventory({ isLoggedIn, onLoggedInStateReady: () => { } }));
    }, [dispatch, isLoggedIn]);

    const { brands, isBrand } = useBrand();
    const isKbbBranded = isBrand(brands.KBB_BRAND);

    listing.birfParams = {
        co_txt_url: 'save',
        c: listing?.owner?.id,
        v: listing.id,
        par: 'v_lstg',
    };

    useEffect(() => {
        // Only call this function on KBB
        if (isKbbBranded) {
            initializeUserInfo(); // Hydrate saved inventory
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = useCallback((event) => {
        dispatch(saveVDPListing({
            event,
            listing,
            wasSaved: isSaved,
            sendClick,
        }));
    }, [dispatch, listing, isSaved]);

    return isValidElement(children) && (
        <div
            data-cmp="saveAndShareOverlay"
            className="display-flex flex-column justify-content-center"
        >
            <div
                className="overlay-actions-container"
                style={{ height: '0', zIndex: '10' }}
            >
                <InventoryListingOverlayActions
                    listing={listing}
                    onSaveToggle={handleSave}
                    isSaved={isSaved}
                    uiContext={uiContext}
                    additionalCta={isShareEnabled && (
                        <ShareContentContainer
                            key="share-glyph"
                            glyphColor="accent"
                            uiContext="glyph"
                            className="display-flex margin-right-4"
                        />
                    )}
                />
            </div>
            {children}
        </div>
    );
}

export default memo(SaveAndShareContainer);
